/**
 * (무인증) 방문자 QR
 */
export interface VisitorQr {
  userId?: number; // 사용자ID
  cardSerialNumber?: string; // CSN
  creationDate?: string; // 카드 생성일자
  expirationDate?: string; // 카드 만료일자
}

/**
 * (무인증) 방문자 QR
 */
export interface VisitorQrResponseData {
  qr?: VisitorQr;
}

export type ConsentAgree = "DENIED" | "GRANTED"; //비동의 | 동의

export type ContractVisitorId = number | string;
type Nullable<T> = T | null;

export type VisitorListSearchType =
  | "ALL"
  | "VISITOR_NAME"
  | "VISITOR_MOBILE_NUMBER"
  | "VISITOR_EMAIL"
  | "VISITOR_CAR_NUMBER"
  | "VISIT_APPLY_NUMBER"
  | "SPACE_PRODUCT_ID"
  | "SPACE_PRODUCT_NAME"
  | "INVITEE_MEMBER_NO";

export enum VisitPurpose {
  /** 업무 미팅 */
  WORK_MEETING = "WORK_MEETING",
  /** 지인 방문 */
  FRIEND_VISIT = "FRIEND_VISIT",
  /** 퀵/택배 */
  PACKAGE_DELIVERY = "PACKAGE_DELIVERY",
  /** 음식 배달 */
  FOOD_DELIVERY = "FOOD_DELIVERY",
  /** 기타 */
  ETC = "ETC",
}

export enum VisitStatus {
  /** 사용 */
  VISIT_USE = "VISIT_USE",
  /** 일시정지 */
  VISIT_PAUSE = "VISIT_PAUSE",
  /** 삭제 */
  VISIT_DELETE = "VISIT_DELETE",
  /** 종료 */
  VISIT_CLOSE = "VISIT_CLOSE",
  /** 대기 */
  VISIT_PENDING = "VISIT_PENDING",
}

export enum VisitType {
  /** 초대 */
  INVITE_REGIST = "INVITE_REGIST",
  /** 셀프 등록 */
  SELF_REGIST = "SELF_REGIST",
  /** 공간예약 등록 */
  S2_REGIST = "S2_REGIST",
}

export enum InvitationChannel {
  /* 공간예약(S2) 상품 결재한 경우 */
  TAAP_SPACE = "TAAP_SPACE",
  /* Mobile App 이용자가 방문자 초대한 경우 */
  MOBILE_APP = "MOBILE_APP",
  /* Ctrl.room 파트너회원/이용자 자격으로 방문자 초대한 경우 */
  CTRL_ROOM = "CTRL_ROOM",
  /* 셀프 방문등록한 경우 */
  SELF_REGIST = "SELF_REGIST",
}

export enum ApprovalStatus {
  /* 자동승인 */
  APPROVAL_AUTO = "APPROVAL_AUTO",
  /* 승인대기 */
  APPROVAL_PENDING = "APPROVAL_PENDING",
  /* 승인완료 */
  APPROVAL_COMPLETE = "APPROVAL_COMPLETE",
  /* 승인반려 */
  APPROVAL_REJECT = "APPROVAL_REJECT",
}

export type VisitorListSortProperty =
  | "contractManageId"
  | "contractApplyNumber"
  | "visitApplyNumber"
  | "visitStartTime"
  | "visitEndTime"
  | "buildingId"
  | "partnerId"
  | "workOrderSheetId"
  | "reservationId"
  | "visitPurpose"
  | "visitPurposeDescription"
  | "visitType"
  | "visitStatus"
  | "spaceProductId"
  | "contractVisitorId"
  | "visitorName"
  | "visitorMobileNumber"
  | "visitorEmail"
  | "visitorCarNumber"
  | "preDiscountId"
  | "contractVisitorGroupId"
  | "contractName"
  | "inviteeMemberId"
  | "inviteeMemberNo"
  | "createdDate"
  | "invitationChannel"
  | "invitationDate"
  | "visitorGuideMemo"
  | "approvalRequestMemo"
  | "approvalStatus"
  | "approvalAuthMemo"
  | "approvalDate"
  | "approverMemberId"
  | "approverMemberNo";

export interface VisitorListSortOrder {
  property: VisitorListSortProperty;
  direction: "ASC" | "DESC";
  ignoreCase?: boolean;
  nullHandling?: "NATIVE" | "NULLS_FIRST" | "NULLS_LAST";
}

export interface VisitorListParams {
  contractApplyNumber?: string; // 이용신청 번호
  contractManageId?: number | string; // 계약 관리 id
  searchType?: VisitorListSearchType; // 검색대상
  searchValue?: string; // 검색어
  visitStatus?: VisitStatus; // 방문상태
  visitStatuses?: VisitStatus[]; // 방문상태 (복수). 비어있지 않으면 visitStatus 보다 우선
  contractManageIds?: (number | string)[]; // 계약 관리 id (복수). 조회 범위를 대상 계약으로 한정
  visitPurpose?: string; // 방문목적
  visitType?: VisitType; // 방문타입
  workOrderSheetId?: number | string; // 워크시트 ID
  reservationId?: number | string; // 예약 ID
  inviteeMemberNo?: string; // 초대자 회원번호
  searchStartTime?: string; // 검색일(From): 방문일시 시작
  searchEndTime?: string; // 검색일(To): 방문일시 종료
  searchStartInvitationDate?: string; // 검색일(From): 초대일시 시작
  searchEndInvitationDate?: string; // 검색일(To): 초대일시 종료
  page?: number;
  size?: number;
  sort?: {
    orders?: VisitorListSortOrder[];
  };
}

export interface VisitorListItem {
  contractManageId?: number | string; // 계약 관리 id
  contractApplyNumber?: string; // 이용신청 번호
  visitApplyNumber?: string; // 방문신청 번호
  contractVisitorId?: ContractVisitorId; // 방문자 ID
  visitorName?: string; // 방문자 이름
  visitorMobileNumber?: string; // 방문자 휴대폰번호
  visitorEmail?: string; // 방문자 이메일
  visitorEmailLang?: string; // 방문자 이메일 언어
  visitorCarNumber?: string; // 방문자 차량번호
  visitorNameCount?: number | string; // 동반 방문자 이름 수
  visitorMobileNumberCount?: number | string; // 동반 방문자 휴대폰번호 수
  visitorEmailCount?: number | string; // 동반 방문자 이메일 수
  visitorCarNumberCount?: number | string; // 동반 방문자 차량번호 수
  visitStartTime?: string; // 방문일시 (시작)
  visitEndTime?: string; // 방문일시 (종료)
  visitStatus?: VisitStatus; // 방문상태
  buildingId?: number | string; // (방문) 건물 ID
  partnerId?: number | string; // 파트너 ID
  workOrderSheetId?: number | string; // 워크시트 ID
  reservationId?: number | string; // 예약 ID
  visitPurpose?: string; // 방문목적
  visitPurposeDescription?: string; // 방문목적이 'ETC'인 경우 상세 내용 기재
  visitType?: VisitType; // 방문타입
  spaceProductId?: number | string; // 공간상품 id
  spaceProductName?: string; // 공간상품이름
  contractName?: string; // 계약명
  contractVisitorGroupId?: number | string; // 방문자 그룹 ID
  inviteeMemberId?: string; // 초대자 ID
  inviteeMemberNo?: string; // 초대자 회원번호
  inviteeNickname?: string; // 초대자 닉네임
  inviteePhoneNumber?: string; // 초대자 휴대폰번호
  inviteeEmail?: string; // 초대자 이메일
  invitationChannel?: InvitationChannel; // 초대경로
  invitationDate?: string; // 초대일시
  approvalStatus?: ApprovalStatus; // 방문초대 승인 상태
  approvalDate?: string; // 방문초대 승인일시
  memberApprovalStatus?: ApprovalStatus; // 멤버 승인 상태
  memberApprovalDate?: string; // 멤버 승인일시
  visitorApproverMemberType?: string; // 방문 승인자 멤버 타입
  meetingPlace?: string; // 만남장소
  preDiscountId?: number | string; // 사전 할인 ID
  parkingVendorCode?: string; // 주차 벤더 코드
  accessUpdatedAt?: string; // 마지막 출입기록 갱신일시
  organizationName?: string; // 조직명
  isDeleted?: boolean; // 삭제 여부
  createdDate?: string; // 생성일
  createdBy?: string; // 생성자ID
  modifiedDate?: string; // 수정일
  modifiedBy?: string; // 수정자ID
}

export interface VisitorListResponseData {
  contractVisitor?: VisitorListItem[];
}

// TCV53. 방문자 삭제 (초대자) 파라미터
export type VisitorDeleteParams = {
  inviteeMemberNo?: string; // 초대자 회원번호 (path). 삭제 대상 이용자의 회원번호
  contractVisitorIds?: ContractVisitorId[]; // 삭제할 방문자 id 목록. 한 번에 복수 전달 가능
};

// TCV53. 방문자 삭제 (초대자) 응답 data
export type VisitorDeleteResponseData = {
  inviteeMemberNo?: string; // 초대자 회원번호
  contractVisitorList?: ContractVisitorId[]; // 삭제된 방문자 id 목록
};

export type VisitorDeletedDisplay = "TRUE" | "FALSE";

export interface VisitorGroupListParams {
  reservationId?: number | string; // 예약 ID
  contractManageId?: number | string; // 계약 관리 id
  contractVisitorGroupIds?: string; // 방문자 그룹 ID 목록 (쉼표 구분 문자열)
  searchStartTime?: string; // 검색일(From): 방문일시 (시작)
  searchEndTime?: string; // 검색일(To): 방문일시 (종료)
  isDeletedVisitorDisplay?: VisitorDeletedDisplay; // 삭제 방문자 노출 여부 (TRUE: 노출, FALSE: 비노출)
  page?: number;
  size?: number;
  sort?: {
    orders?: VisitorListSortOrder[];
  };
}

export interface VisitorGroupVisitorItem {
  contractVisitorId?: Nullable<ContractVisitorId>; // 방문자 ID
  contractVisitorGroupId?: Nullable<number | string>; // 방문자 그룹 ID
  visitorName?: Nullable<string>; // 방문자 이름
  visitorMobileNumber?: Nullable<string>; // 방문자 휴대폰번호
  visitorEmail?: Nullable<string>; // 방문자 이메일
  visitorEmailLang?: Nullable<string>; // 방문자 이메일 언어
  visitorCarNumber?: Nullable<string>; // 방문자 차량번호
  organizationName?: Nullable<string>; // 방문자 소속회사
  preDiscountId?: Nullable<number | string>; // 주차 사전할인 ID
  parkingVendorCode?: Nullable<string>; // 주차시스템 업체
  accessUpdatedAt?: Nullable<string>; // 최근 출입일시
  isDeleted?: Nullable<boolean>; // 삭제여부
  createdDate?: Nullable<string>; // 생성일
  createdBy?: Nullable<string>; // 생성자
  modifiedDate?: Nullable<string>; // 수정일
  modifiedBy?: Nullable<string>; // 수정자
}

export interface VisitorGroupListItem {
  contractVisitorGroupId?: Nullable<number | string>; // 방문자 그룹 id
  contractManageId?: Nullable<number | string>; // 계약 관리 id
  visitApplyNumber?: Nullable<string>; // 방문신청 번호
  visitStartTime?: Nullable<string>; // 방문일시 (시작)
  visitEndTime?: Nullable<string>; // 방문일시 (종료)
  buildingId?: Nullable<number | string>; // 방문건물 ID
  meetingPlace?: Nullable<string>; // 만남장소
  workOrderSheetId?: Nullable<number | string>; // 워크시트 ID
  visitPurpose?: Nullable<string>; // 방문목적
  visitPurposeDescription?: Nullable<string>; // 방문목적이 'ETC'인 경우 상세 내용 기재
  visitType?: Nullable<VisitType>; // 방문타입
  visitStatus?: Nullable<VisitStatus>; // 방문상태
  inviteeMemberId?: Nullable<string>; // 초대자 ID
  inviteeMemberNo?: Nullable<string>; // 초대자 회원번호
  invitationChannel?: Nullable<InvitationChannel>; // 초대경로
  invitationDate?: Nullable<string>; // 초대일시
  visitorGuideMemo?: Nullable<string>; // 방문자 안내 메모
  memberApprovalStatus?: Nullable<ApprovalStatus>; // 이용자 승인상태
  memberApprovalAuthMemo?: Nullable<string>; // 이용자 승인메모
  memberApprovalDate?: Nullable<string>; // 이용자 승인일시
  memberApproverMemberNo?: Nullable<string>; // 이용자(승인자) 회원번호
  approvalRequestMemo?: Nullable<string>; // 관리자 승인요청 메모
  approvalStatus?: Nullable<ApprovalStatus>; // 관리자 승인상태
  approvalAuthMemo?: Nullable<string>; // 관리자 승인메모
  approvalDate?: Nullable<string>; // 관리자 승인일시
  approverMemberId?: Nullable<string>; // 관리자(승인자) ID
  approverMemberNo?: Nullable<string>; // 관리자(승인자) 회원번호
  reservationId?: Nullable<number | string>; // 예약 ID
  createdDate?: Nullable<string>; // 등록일자
  createdBy?: Nullable<string>; // 등록자ID
  modifiedDate?: Nullable<string>; // 수정일자
  modifiedBy?: Nullable<string>; // 수정자
  contractVisitorList?: VisitorGroupVisitorItem[];
  accessGroupAddIdsList?: Array<number | string>;
}

export interface VisitorGroupListResponseData {
  contractVisitorGroup?: VisitorGroupListItem[];
}

export interface VisitorGroupDetailParams {
  visitApplyNumber?: string; // 방문신청 번호
}

export interface VisitorGroupDetailResponseData {
  contractVisitorGroup?: VisitorGroupListItem;
}

export type VisitorInviteGroupPageListSortProperty =
  | "contractVisitorGroupId"
  | "visitApplyNumber"
  | "visitStartTime"
  | "visitEndTime"
  | "meetingPlace"
  | "workOrderSheetId"
  | "reservationId"
  | "visitPurpose"
  | "visitPurposeDescription"
  | "visitType"
  | "visitStatus"
  | "inviteeMemberId"
  | "inviteeMemberNo"
  | "invitationChannel"
  | "invitationDate"
  | "visitorGuideMemo"
  | "approvalRequestMemo"
  | "memberApprovalStatus"
  | "memberApprovalDate"
  | "memberApprovalAuthMemo"
  | "memberApproverMemberNo"
  | "approvalStatus"
  | "approvalDate"
  | "approvalAuthMemo"
  | "approverMemberId"
  | "approverMemberNo"
  | "createdDate"
  | "modifiedDate";

export interface VisitorInviteGroupPageListSortOrder {
  property: VisitorInviteGroupPageListSortProperty;
  direction: "ASC" | "DESC";
  ignoreCase?: boolean;
  nullHandling?: "NATIVE" | "NULLS_FIRST" | "NULLS_LAST";
}

export interface VisitorInviteGroupPageListParams {
  contractManageId?: number | string; // 계약 관리 id
  searchStartTime?: string; // 검색일(From): 방문일시 (시작)
  searchEndTime?: string; // 검색일(To): 방문일시 (종료)
  searchStartInvitationDate?: string; // 검색일(From): 초대일시 (시작)
  searchEndInvitationDate?: string; // 검색일(To): 초대일시 (종료)
  page?: number;
  size?: number;
  sort?: {
    orders?: VisitorInviteGroupPageListSortOrder[];
  };
}

export type VisitorInviteGroupPageListItem = Omit<VisitorGroupListItem, "contractVisitorList" | "accessGroupAddIdsList">;

export interface VisitorInviteGroupPageListResponseData {
  contractVisitorGroup?: VisitorInviteGroupPageListItem[];
  contractVisitor?: VisitorInviteGroupPageListItem[];
}

/**
 * (무인증) 방문자 초내내역 정보
 */
export interface Visitor {
  spaceProductId?: number; // 공간상품 id
  spaceProductName?: string; // 상품명
  buildingLocationCode?: string; // 방문건물 Location Code (방문건물 정보가 없을 경우 대표건물 정보)
  buildingId?: number; // 방문건물 id (방문건물 정보가 없을 경우 대표건물 정보)
  mbOrganizationId?: number; // 조직(회사) id
  visitApplyNumber?: string; // 방문신청 번호
  contractVisitorId?: ContractVisitorId; // 방문자 id
  visitorName?: string; // 방문자 이름
  visitorMobileNumber?: string; // 방문자 휴대폰번호
  visitorEmail?: string; // 방문자 이메일
  visitorEmailLang?: string; // 방문자 이메일 언어
  visitorCarNumber?: string; // 방문자 차량번호
  visitStartTime?: string; // 방문일시 (시작)
  visitEndTime?: string; // 방문일시 (종료)
  visitStatus?: VisitStatus; // 방문상태
  meetingPlace?: string; // 만남장소
  contractVisitorList?: ContractVisitorId[]; // 계약방문자 id 목록
  visitPurpose?: string; // 방문 목적
  visitPurposeDescription?: string; // 방문목적이 'ETC'인 경우 상세 내용 기재
  visitType?: VisitType; //방문 타입
  consentAgree?: ConsentAgree;
  isKpsFeatureOn?: boolean;
  contractName?: string;
}

/**
 * (무인증) 방문자 등록차량 정보 수정 api response data
 */
export interface VisitorUpdateResponseData {
  visitApplyNumber: string; // 방문신청 번호
  contractVisitorId: ContractVisitorId; // 방문자 id
}

/**
 * (무인증) (무인증)방문자용 개인정보 이용 약관 동의 저장 api response data
 */
export interface VisitorPatchResponseData {
  form: any; //
  status: any; // 폰 뒷 4 자리
  userKey: any;
}

/**
 * TCV60. 방문자 셀프 등록 승인 이용자 유형
 *  - MEMBER_INVITER : 이용자(초대자)
 *  - MEMBER_ADMIN   : 이용자(관리자)
 */
export enum VisitorApproverMemberType {
  MEMBER_INVITER = "MEMBER_INVITER",
  MEMBER_ADMIN = "MEMBER_ADMIN",
}

/**
 * TCV60. 셀프등록 방문자 그룹 목록 조회 요청 파라미터
 */
export interface CtSelfVisitGroupListReqParam {
  contractApplyNumber: string;
  searchStartTime?: string;
  searchEndTime?: string;
  memberApprovalStatus?: ApprovalStatus;
  page: number;
  size: number;
}

/**
 * TCV60. 이용자 승인상태별 카운트
 * NONE 등 spec에 명시되지 않은 키도 응답에 포함될 수 있음
 */
export interface MemberApprovalStatusCount {
  APPROVAL_PENDING?: number;
  APPROVAL_COMPLETE?: number;
  APPROVAL_REJECT?: number;
  APPROVAL_AUTO?: number;
  NONE?: number;
}

/**
 * TCV60. 셀프등록 방문자 그룹 목록 항목
 */
export interface CtSelfVisitGroupListItem {
  contractManageId: number;
  contractApplyNumber: string;
  visitApplyNumber: string;
  contractVisitorGroupId: number;

  /** 방문자 이름 (대표 + " 외 n-1명") */
  visitorName: string;
  /** 방문자 휴대폰번호 (대표 + " 외 n-1개") */
  visitorMobileNumber: string;
  /** 방문자 차량번호 (대표 + " 외 n-1개") */
  visitorCarNumber: string;
  /** 방문자수 */
  visitorNums: number;
  /** 방문자 차량수 */
  visitorCarNums: number;

  mainVisitorName: string;
  mainVisitorMobileNumber: string;
  mainVisitorCarNumber: string;

  /** 방문일시 (시작) */
  visitStartTime: string;
  /** 방문일시 (종료) */
  visitEndTime: string;
  /** 방문상태 (대기/사용/삭제/종료) */
  visitStatus: VisitStatus;

  /** 방문자 셀프 등록 승인 이용자 유형 */
  visitorApproverMemberType: VisitorApproverMemberType;
  /** 이용자 승인상태 (승인대기/승인완료/승인반려) */
  memberApprovalStatus: ApprovalStatus | "";
  /** 이용자 승인일시 */
  memberApprovalDate: string;
  /** 관리자 승인상태 (자동승인/승인대기/승인완료/승인반려) */
  approvalStatus: ApprovalStatus;
  /** 관리자 승인일시 */
  approvalDate: string;

  spaceProductId: number;
  /** 공간상품이름 (스냅샷) */
  spaceProductName: string;

  contractName: string;
  /** 방문건물 ID (방문건물 정보가 없을 경우 대표건물 정보) */
  buildingId: number;

  isDeleted?: boolean;

  createdDate: string;
  modifiedDate: string;
}

/**
 * TCV60. 셀프등록 방문자 그룹 목록 조회 응답 (data 영역)
 */
export interface CtSelfVisitGroupListResponse {
  memberApprovalStatusCount?: MemberApprovalStatusCount;
  content: Array<CtSelfVisitGroupListItem>;
}

/**
 * (무인증) 셀프등록 방문자 초내내역 정보
 */
